/**
 * Upgrade system.
 *
 * Upgrades are pure data: each level describes a modifier applied to the base
 * `CarHandling` of the vehicle. `computeHandling` folds them together, so an
 * upgrade genuinely changes the physics the simulator runs — nothing here is
 * cosmetic bookkeeping. Several upgrades deliberately trade one stat for
 * another (turbo adds weight, aero adds drag, transmission trades launch for
 * top speed) which keeps the fastest car from being the best at everything.
 */

import type { CarDef, CarHandling, CarStats } from './cars.js';
import type { SurfaceId } from './surfaces.js';

export type UpgradeId =
  | 'engine'
  | 'turbo'
  | 'tires'
  | 'suspension'
  | 'brakes'
  | 'transmission'
  | 'weight'
  | 'differential'
  | 'aero';

export interface UpgradeEffect {
  engineForceMul?: number;
  maxSpeedMul?: number;
  brakeForceMul?: number;
  muBaseAdd?: number;
  steerMaxMul?: number;
  steerRateMul?: number;
  cornerStiffnessMul?: number;
  massMul?: number;
  dragMul?: number;
  rollResistMul?: number;
  downforceAdd?: number;
  handbrakeMuMul?: number;
  inertiaScaleMul?: number;
  turboForceMul?: number;
  turboTimeAdd?: number;
  /** Grip gained on loose/low-traction surfaces (gravel, dirt, mud, sand, ice). */
  looseGripAdd?: number;
}

export interface UpgradeLevel {
  cost: number;
  effect: UpgradeEffect;
  note: string;
}

export interface UpgradeDef {
  id: UpgradeId;
  name: string;
  /** One-line summary shown in the garage. */
  description: string;
  /** Longer explanation of the trade-off, shown in the detail panel. */
  tradeoff: string;
  icon: string;
  levels: UpgradeLevel[];
}

export const UPGRADES: UpgradeDef[] = [
  {
    id: 'engine',
    name: 'Engine',
    description: 'Stronger internals raise drive force and the rev ceiling.',
    tradeoff: 'Slightly thirstier and a touch heavier, but no real downside.',
    icon: '⚙',
    levels: [
      { cost: 1200, effect: { engineForceMul: 0.05, maxSpeedMul: 0.02 }, note: '+5% power, +2% top speed' },
      { cost: 2400, effect: { engineForceMul: 0.05, maxSpeedMul: 0.02 }, note: '+5% power, +2% top speed' },
      { cost: 4300, effect: { engineForceMul: 0.06, maxSpeedMul: 0.025 }, note: '+6% power, +2.5% top speed' },
      { cost: 7400, effect: { engineForceMul: 0.06, maxSpeedMul: 0.025 }, note: '+6% power, +2.5% top speed' },
      { cost: 12200, effect: { engineForceMul: 0.07, maxSpeedMul: 0.03 }, note: '+7% power, +3% top speed' },
      { cost: 19500, effect: { engineForceMul: 0.08, maxSpeedMul: 0.035 }, note: '+8% power, +3.5% top speed' },
    ],
  },
  {
    id: 'turbo',
    name: 'Turbo',
    description: 'Forced induction adds a burst of power you can deploy on straights.',
    tradeoff: 'The plumbing and intercooler add weight, which dulls turn-in.',
    icon: '🌀',
    levels: [
      { cost: 2200, effect: { turboForceMul: 0.5, engineForceMul: 0.02, massMul: 0.015 }, note: '+50% boost force, +1.5% weight' },
      { cost: 4100, effect: { turboForceMul: 0.55, engineForceMul: 0.02, massMul: 0.015, turboTimeAdd: 0.2 }, note: '+55% boost force, +0.2s' },
      { cost: 7200, effect: { turboForceMul: 0.6, engineForceMul: 0.03, massMul: 0.02, turboTimeAdd: 0.25 }, note: '+60% boost force, +0.25s' },
      { cost: 11800, effect: { turboForceMul: 0.7, engineForceMul: 0.03, massMul: 0.02, turboTimeAdd: 0.3 }, note: '+70% boost force, +0.3s' },
      { cost: 18400, effect: { turboForceMul: 0.85, engineForceMul: 0.04, massMul: 0.025, turboTimeAdd: 0.35 }, note: '+85% boost force, +0.35s' },
    ],
  },
  {
    id: 'tires',
    name: 'Tires',
    description: 'Stickier compound lifts the friction ceiling on every surface.',
    tradeoff: 'Sticky rubber wears faster — durability drops as grip climbs.',
    icon: '◍',
    levels: [
      { cost: 1400, effect: { muBaseAdd: 0.08, looseGripAdd: 0.05 }, note: '+0.08 friction' },
      { cost: 2800, effect: { muBaseAdd: 0.08, looseGripAdd: 0.06 }, note: '+0.08 friction' },
      { cost: 4900, effect: { muBaseAdd: 0.09, looseGripAdd: 0.07 }, note: '+0.09 friction' },
      { cost: 8300, effect: { muBaseAdd: 0.1, looseGripAdd: 0.08 }, note: '+0.10 friction' },
      { cost: 13400, effect: { muBaseAdd: 0.11, looseGripAdd: 0.1 }, note: '+0.11 friction' },
      { cost: 21000, effect: { muBaseAdd: 0.12, looseGripAdd: 0.12 }, note: '+0.12 friction' },
    ],
  },
  {
    id: 'suspension',
    name: 'Suspension',
    description: 'Better damping keeps the tyres planted and sharpens response.',
    tradeoff: 'Stiff setups are less forgiving on rough surfaces.',
    icon: '⌇',
    levels: [
      { cost: 1100, effect: { steerRateMul: 0.07, cornerStiffnessMul: 0.04 }, note: '+7% steering rate' },
      { cost: 2300, effect: { steerRateMul: 0.07, cornerStiffnessMul: 0.04 }, note: '+7% steering rate' },
      { cost: 4100, effect: { steerRateMul: 0.08, cornerStiffnessMul: 0.05, rollResistMul: 0.98 }, note: '+8% steering rate' },
      { cost: 7000, effect: { steerRateMul: 0.08, cornerStiffnessMul: 0.05, rollResistMul: 0.98 }, note: '+8% steering rate' },
      { cost: 11500, effect: { steerRateMul: 0.09, cornerStiffnessMul: 0.06, rollResistMul: 0.97 }, note: '+9% steering rate' },
      { cost: 18000, effect: { steerRateMul: 0.1, cornerStiffnessMul: 0.07, rollResistMul: 0.97 }, note: '+10% steering rate' },
    ],
  },
  {
    id: 'brakes',
    name: 'Brakes',
    description: 'Bigger discs and better cooling shorten braking distances.',
    tradeoff: 'Adds a little unsprung weight.',
    icon: '⊘',
    levels: [
      { cost: 900, effect: { brakeForceMul: 0.1 }, note: '+10% braking force' },
      { cost: 2000, effect: { brakeForceMul: 0.1, massMul: 0.004 }, note: '+10% braking force' },
      { cost: 3600, effect: { brakeForceMul: 0.12, massMul: 0.004 }, note: '+12% braking force' },
      { cost: 6200, effect: { brakeForceMul: 0.12, massMul: 0.005 }, note: '+12% braking force' },
      { cost: 10200, effect: { brakeForceMul: 0.14, massMul: 0.005 }, note: '+14% braking force' },
      { cost: 16000, effect: { brakeForceMul: 0.15, massMul: 0.006 }, note: '+15% braking force' },
    ],
  },
  {
    id: 'transmission',
    name: 'Transmission',
    description: 'Longer final drive and closer ratios lift the top end.',
    tradeoff: 'Trading launch for top speed costs a little low-end punch.',
    icon: '⚏',
    levels: [
      { cost: 1600, effect: { maxSpeedMul: 0.04, engineForceMul: -0.01, massMul: 0.004 }, note: '+4% top speed, -1% launch' },
      { cost: 3200, effect: { maxSpeedMul: 0.04, engineForceMul: -0.01, massMul: 0.004 }, note: '+4% top speed, -1% launch' },
      { cost: 5500, effect: { maxSpeedMul: 0.05, engineForceMul: -0.015, massMul: 0.005 }, note: '+5% top speed, -1.5% launch' },
      { cost: 9200, effect: { maxSpeedMul: 0.05, engineForceMul: -0.015, massMul: 0.005 }, note: '+5% top speed, -1.5% launch' },
      { cost: 15000, effect: { maxSpeedMul: 0.06, engineForceMul: -0.02, massMul: 0.006 }, note: '+6% top speed, -2% launch' },
    ],
  },
  {
    id: 'weight',
    name: 'Weight Reduction',
    description: 'Strip sound deadening and swap panels for composites.',
    tradeoff: 'Lighter cars are faster everywhere but easier to unsettle.',
    icon: '⇓',
    levels: [
      { cost: 1800, effect: { massMul: -0.03, inertiaScaleMul: 0.97 }, note: '-3% weight' },
      { cost: 3600, effect: { massMul: -0.03, inertiaScaleMul: 0.97 }, note: '-3% weight' },
      { cost: 6200, effect: { massMul: -0.035, inertiaScaleMul: 0.965 }, note: '-3.5% weight' },
      { cost: 10500, effect: { massMul: -0.035, inertiaScaleMul: 0.965 }, note: '-3.5% weight' },
      { cost: 17000, effect: { massMul: -0.04, inertiaScaleMul: 0.96 }, note: '-4% weight' },
    ],
  },
  {
    id: 'differential',
    name: 'Differential',
    description: 'Adjustable LSD puts the power down and lets you steer on the throttle.',
    tradeoff: 'A locked diff resists rotation in slow corners.',
    icon: '◈',
    levels: [
      { cost: 2400, effect: { cornerStiffnessMul: 0.05, muBaseAdd: 0.04, handbrakeMuMul: 0.94 }, note: '+5% grip, easier slides' },
      { cost: 4600, effect: { cornerStiffnessMul: 0.05, muBaseAdd: 0.04, handbrakeMuMul: 0.94 }, note: '+5% grip, easier slides' },
      { cost: 8000, effect: { cornerStiffnessMul: 0.06, muBaseAdd: 0.05, handbrakeMuMul: 0.92 }, note: '+6% grip, easier slides' },
      { cost: 13200, effect: { cornerStiffnessMul: 0.06, muBaseAdd: 0.05, handbrakeMuMul: 0.92 }, note: '+6% grip, easier slides' },
    ],
  },
  {
    id: 'aero',
    name: 'Aerodynamics',
    description: 'Splitters and wings create downforce that grows with speed.',
    tradeoff: 'Drag rises, so you bleed speed on long straights.',
    icon: '⋏',
    levels: [
      { cost: 2000, effect: { downforceAdd: 0.16, dragMul: 1.03, massMul: 0.006 }, note: '+0.16 downforce, +3% drag' },
      { cost: 4000, effect: { downforceAdd: 0.18, dragMul: 1.03, massMul: 0.006 }, note: '+0.18 downforce, +3% drag' },
      { cost: 6800, effect: { downforceAdd: 0.2, dragMul: 1.035, massMul: 0.007 }, note: '+0.20 downforce, +3.5% drag' },
      { cost: 11400, effect: { downforceAdd: 0.22, dragMul: 1.035, massMul: 0.007 }, note: '+0.22 downforce, +3.5% drag' },
      { cost: 18600, effect: { downforceAdd: 0.25, dragMul: 1.04, massMul: 0.008 }, note: '+0.25 downforce, +4% drag' },
    ],
  },
];

export const UPGRADE_BY_ID: Record<string, UpgradeDef> = UPGRADES.reduce(
  (acc, u) => {
    acc[u.id] = u;
    return acc;
  },
  {} as Record<string, UpgradeDef>,
);

export type UpgradeLevels = Partial<Record<UpgradeId, number>>;

export const EMPTY_UPGRADES: UpgradeLevels = {};

export function totalUpgradeCost(): number {
  let total = 0;
  for (const u of UPGRADES) for (const l of u.levels) total += l.cost;
  return total;
}

/** Cost of buying the next level of an upgrade, or null when fully upgraded. */
export function nextUpgradeCost(id: string, currentLevel: number): number | null {
  const def = UPGRADE_BY_ID[id];
  if (!def) return null;
  const next = def.levels[currentLevel];
  return next ? next.cost : null;
}

const LOOSE_SURFACES: SurfaceId[] = ['gravel', 'dirt', 'mud', 'sand', 'ice', 'grass'];

/**
 * Applies every purchased upgrade level to a car's base handling and returns a
 * *new* handling record. Never mutates the shared car definition.
 */
export function computeHandling(car: CarDef, levels: UpgradeLevels | undefined): CarHandling {
  const base = car.handling;
  if (!levels) return { ...base, gripBonus: { ...base.gripBonus } };
  let h: CarHandling = { ...base, gripBonus: { ...base.gripBonus } };
  for (const def of UPGRADES) {
    const level = levels[def.id] ?? 0;
    if (level <= 0) continue;
    for (let i = 0; i < Math.min(level, def.levels.length); i++) {
      h = applyEffect(h, def.levels[i]!.effect);
    }
  }
  // Safety clamps so no upgrade chain can produce nonsense physics.
  h.mass = Math.max(320, h.mass);
  h.engineForce = Math.max(20000, h.engineForce);
  h.brakeForce = Math.max(30000, h.brakeForce);
  h.maxSpeed = Math.max(120, h.maxSpeed);
  h.muBase = Math.max(0.4, h.muBase);
  h.drag = Math.max(0.05, h.drag);
  h.rollResist = Math.max(2, h.rollResist);
  h.steerMax = Math.min(1.1, Math.max(0.25, h.steerMax));
  h.downforce = Math.max(0, h.downforce);
  return h;
}

function applyEffect(h: CarHandling, e: UpgradeEffect): CarHandling {
  const next: CarHandling = { ...h, gripBonus: { ...h.gripBonus } };
  if (e.engineForceMul) next.engineForce *= 1 + e.engineForceMul;
  if (e.maxSpeedMul) next.maxSpeed *= 1 + e.maxSpeedMul;
  if (e.brakeForceMul) next.brakeForce *= 1 + e.brakeForceMul;
  if (e.muBaseAdd) next.muBase += e.muBaseAdd;
  if (e.steerMaxMul) next.steerMax *= 1 + e.steerMaxMul;
  if (e.steerRateMul) next.steerRate *= 1 + e.steerRateMul;
  if (e.cornerStiffnessMul) next.cornerStiffness *= 1 + e.cornerStiffnessMul;
  if (e.massMul) next.mass *= 1 + e.massMul;
  if (e.dragMul) next.drag *= e.dragMul;
  if (e.rollResistMul) next.rollResist *= e.rollResistMul;
  if (e.downforceAdd) next.downforce += e.downforceAdd;
  if (e.handbrakeMuMul) next.handbrakeMu *= e.handbrakeMuMul;
  if (e.inertiaScaleMul) next.inertiaScale *= e.inertiaScaleMul;
  if (e.turboForceMul) next.turboForce *= 1 + e.turboForceMul;
  if (e.turboTimeAdd) next.turboTime += e.turboTimeAdd;
  if (e.looseGripAdd) {
    for (const s of LOOSE_SURFACES) next.gripBonus[s] = (next.gripBonus[s] ?? 0) + e.looseGripAdd;
  }
  return next;
}

const stat01 = (v: number, lo: number, hi: number): number => Math.round(Math.min(10, Math.max(1, ((v - lo) / (hi - lo)) * 9 + 1)));

/** Derives the 1..10 garage stats from a tuning-adjusted handling record. */
export function handlingToStats(h: CarHandling): CarStats {
  return {
    accel: stat01(h.engineForce / h.mass, 60, 190),
    topSpeed: stat01(h.maxSpeed, 260, 540),
    handling: stat01(h.steerMax * 100 + h.cornerStiffness * 3 - h.mass / 90, 45, 100),
    grip: stat01(h.muBase, 1.4, 2.6),
    braking: stat01(h.brakeForce / h.mass, 120, 220),
    weight: stat01(h.mass, 600, 1900),
    durability: 5,
  };
}

/** Convenience helper used by the client to render current vs. potential stats. */
export function effectiveCar(car: CarDef, levels: UpgradeLevels | undefined): CarDef {
  const handling = computeHandling(car, levels);
  return { ...car, handling, stats: handlingToStats(handling) };
}

/** Sum of every upgrade level expressed as "installation progress" 0..1. */
export function upgradeProgress(levels: UpgradeLevels): number {
  let owned = 0;
  let max = 0;
  for (const u of UPGRADES) {
    owned += Math.min(levels[u.id] ?? 0, u.levels.length);
    max += u.levels.length;
  }
  return max === 0 ? 0 : owned / max;
}
